import { api, type ApiFile, type Page } from '@/shared/api/client';
import { quoteDetailKey, quoteListsKey } from '@/shared/quotes/api';

import type {
  Quote,
  QuoteCopies,
  QuoteCreate,
  QuoteLinesWrite,
  QuotePdfLink,
  QuoteSendWrite,
  QuoteStatus,
  QuoteUpdate,
  QuoteWarningPatch,
  QuoteWarningsWrite,
  ListResolution,
} from './types';

export interface QuoteListParams {
  status?: QuoteStatus;
  /** Only the copies and redos of that quote (contract v10.8). */
  duplicated_from_id?: string;
  limit?: number;
  offset?: number;
}

export const quoteKeys = {
  all: ['quotes'] as const,
  /**
   * Every list, whatever its filters. Narrower than `all` on purpose: see `useQuotes`.
   * Shared with `shared/quotes` (the inbox redoes quotes too): one key, one cache entry.
   */
  lists: quoteListsKey,
  list: (params: QuoteListParams) => ['quotes', 'list', params] as const,
  detail: quoteDetailKey,
};

export const quotesApi = {
  list: (params: QuoteListParams = {}): Promise<Page<Quote>> =>
    api.get<Page<Quote>>('/api/quotes', { ...params }),

  detail: (id: string): Promise<Quote> => api.get<Quote>(`/api/quotes/${id}`),

  create: (payload: QuoteCreate): Promise<Quote> => api.post<Quote>('/api/quotes', payload),

  /** Human approval. This is where the backend assigns the quote number. */
  review: (id: string): Promise<Quote> => api.post<Quote>(`/api/quotes/${id}/review`),

  /**
   * Replace **every** line, in order. The positions come from the list.
   *
   * The cost is not in the payload and must never be: `net_cost`,
   * `supplier_unit_price`, `supplier_discounts` and `price_unit_basis` are what the
   * supplier said, and the API keeps them from the line `keep_line_id` names. A figure on
   * the customer's document that came from a browser form would have no provenance.
   */
  putLines: (id: string, payload: QuoteLinesWrite): Promise<Quote> =>
    api.put<Quote>(`/api/quotes/${id}/lines`, payload),

  /**
   * Recompute prices and totals from the costs already stored.
   *
   * With `marginPct` it applies a global margin **and clears the per-line ones**: "apply
   * 30 % to everything" has to mean everything. It reads nothing from the supplier again,
   * so pressing it twice is free and it can never become a second call to the model.
   */
  recalculate: (id: string, marginPct?: string): Promise<Quote> =>
    api.post<Quote>(
      `/api/quotes/${id}/recalculate${marginPct ? `?margin_pct=${encodeURIComponent(marginPct)}` : ''}`,
    ),

  /** What Víctor does with the offer he decides not to quote. The number is kept. */
  cancel: (id: string): Promise<Quote> => api.post<Quote>(`/api/quotes/${id}/cancel`),

  /**
   * The same quote for other customers (contract v10.8): one identical draft per
   * `client_id`, each to be approved on its own. Idempotent per customer on the back.
   */
  copies: (id: string, clientIds: string[]): Promise<QuoteCopies> =>
    api.post<QuoteCopies>(`/api/quotes/${id}/copies`, { client_ids: clientIds }),

  /**
   * The customer, the validity or the notes. Only what is sent gets written.
   *
   * Pointing a quote at a customer and freezing that customer's card are different
   * moments: this is the first, and it costs nothing to change your mind until the
   * approval.
   */
  update: (id: string, payload: QuoteUpdate): Promise<Quote> =>
    api.patch<Quote>(`/api/quotes/${id}`, payload),

  /**
   * Which of the supplier's notes go on the document: the positions of the ticked ones.
   *
   * A replacement, like the lines: what is sent is the whole answer to "which of these
   * print", and the text never travels back. Only a draft can change it.
   */
  putWarnings: (id: string, payload: QuoteWarningsWrite): Promise<Quote> =>
    api.put<Quote>(`/api/quotes/${id}/warnings`, payload),

  /**
   * One of the supplier's notes, rewritten: its type, its text, or both (contract v10.6).
   *
   * `position` is the index into the API's `warnings`. Only what is sent gets written,
   * and the back keeps the supplier's words in `original_text` from the first rewrite,
   * so nothing the supplier said is lost by correcting how it reads.
   */
  patchWarning: (id: string, position: number, payload: QuoteWarningPatch): Promise<Quote> =>
    api.patch<Quote>(`/api/quotes/${id}/warnings/${position}`, payload),

  /**
   * The PDF was sent some other way (from Thunderbird, by hand) and this records it. What
   * the platform sends itself is `send`.
   */
  markSent: (id: string): Promise<Quote> => api.post<Quote>(`/api/quotes/${id}/sent`),

  /**
   * «Enviar por correo» (contract v10.26, ADR 0022 of the back): the quote's PDF from a
   * Cubas mailbox, through its own outgoing server, and a copy left in its Sent folder.
   * The API answers 202 with the quote and a `pending` sending; the worker sends it and
   * the screen follows `sendings` until it is `sent` or `failed`.
   */
  send: (id: string, payload: QuoteSendWrite): Promise<Quote> =>
    api.post<Quote>(`/api/quotes/${id}/send`, payload),

  /** Only the copy in Sent, again, for a sending that went out and whose copy failed. */
  retryAppend: (id: string, sendingId: string): Promise<Quote> =>
    api.post<Quote>(`/api/quotes/${id}/sendings/${sendingId}/retry-append`),

  /**
   * A signed, temporary URL to the document in the bucket.
   *
   * The bytes never travel through the API: serving them from there would mean loading
   * the whole PDF into memory on every preview. A draft's, which is not in the bucket,
   * is `draftPdf`.
   */
  pdf: (id: string): Promise<QuotePdfLink> => api.get<QuotePdfLink>(`/api/quotes/${id}/pdf`),

  /**
   * The draft's PDF (contract v10.18): how it will print, before approving it.
   *
   * The same document as the final one with BORRADOR where the number goes, today's date
   * and the card of the customer the draft points at. It spends no number and writes
   * nothing, so it can be asked for as often as wanted. The bytes come in the answer:
   * a draft changes with every edit and is never stored in the bucket.
   */
  draftPdf: (id: string): Promise<ApiFile> => api.getFile(`/api/quotes/${id}/draft-pdf`),

  /**
   * The supplier's document, uploaded by hand, on its way to be read (contract v8).
   *
   * One or more files under the `files` field: a PDF, a photo, a spreadsheet, a Word
   * file. The API answers with the quote pointing at the email it just made
   * (`email_id`) and `offer_read_at` still null: the reading is queued, not done. The
   * screen polls until it is.
   */
  uploadOfferDocuments: (id: string, files: File[]): Promise<Quote> => {
    const form = new FormData();
    for (const file of files) form.append('files', file, file.name);
    return api.postForm<Quote>(`/api/quotes/${id}/offer-documents`, form);
  },

  /**
   * Ask the worker for the document again.
   *
   * The approval enqueues **after** committing, so a Redis that was down for that instant
   * leaves a quote approved, numbered and without a PDF for ever: approving again does
   * nothing, because it is no longer a draft. Idempotent by construction, because the
   * object key is the quote number.
   */
  retryPdf: (id: string): Promise<Quote> => api.post<Quote>(`/api/quotes/${id}/pdf`),

  /**
   * «Pegar lista» (contract v10.52): what the tariffs in force say of each line of a
   * customer's list. Writes nothing; the lines chosen go in with `tariff-lines`.
   */
  resolveList: (text: string): Promise<ListResolution> =>
    api.post<ListResolution>('/api/quotes/list/resolve', { text }),

  /** The same with the customer's Excel or CSV, under `file`. Up to 2 MB: 413 past it,
   * 422 when it is not a spreadsheet, both with a reason in Spanish. */
  resolveListFile: (file: File): Promise<ListResolution> => {
    const form = new FormData();
    form.append('file', file, file.name);
    return api.postForm<ListResolution>('/api/quotes/list/resolve-file', form);
  },
};
